import {
	AmbientLight,
	DirectionalLight,
	HemisphereLight,
	PointLight,
	RectAreaLight,
	Scene,
	SpotLight
} from 'three';

import {
	getLightType,
	LIGHT_HELPER_DEFAULTS,
	LightHelperObjects,
	LightType,
	RenderQuality,
	VisualizableLight
} from './light-helper.types';
import {
	createAmbientLightVisualization,
	disposeAmbientLightVisualization,
	updateAmbientLightVisualization
} from './visualize-ambient-light';
import {
	createDirectionalLightVisualization,
	disposeDirectionalLightVisualization,
	updateDirectionalLightVisualization
} from './visualize-directional-light';
import {
	createHemisphereLightVisualization,
	disposeHemisphereLightVisualization,
	updateHemisphereLightVisualization
} from './visualize-hemisphere-light';
import {
	createPointLightVisualization,
	disposePointLightVisualization,
	updatePointLightVisualization
} from './visualize-point-light';
import {
	createRectAreaLightVisualization,
	disposeRectAreaLightVisualization,
	updateRectAreaLightVisualization
} from './visualize-rect-area-light';
import {
	createSpotLightVisualization,
	disposeSpotLightVisualization,
	updateSpotLightVisualization
} from './visualize-spotlight';

/**
 * Creates visualization objects for any light type
 *
 * Factory function that delegates to the appropriate visualization creator
 * based on the light type.
 *
 * @param light The light to visualize
 * @param scene Scene to add objects to
 * @param radius Radius for sphere/cone
 * @param height Height for cone (SpotLight only)
 * @param visible Initial visibility state
 * @param quality Render quality 0-10
 * @returns Light helper objects appropriate for the light type
 */
export const createLightVisualization = (
	light: VisualizableLight,
	scene: Scene,
	radius: number = LIGHT_HELPER_DEFAULTS.CONE_RADIUS,
	height: number = LIGHT_HELPER_DEFAULTS.CONE_HEIGHT,
	visible: boolean = false,
	quality: RenderQuality = LIGHT_HELPER_DEFAULTS.QUALITY
): LightHelperObjects => {
	const lightType = getLightType(light);

	switch (lightType) {
		case LightType.Spot:
			return createSpotLightVisualization(
				light as SpotLight,
				scene,
				radius,
				height,
				visible,
				quality
			);

		case LightType.Directional:
			return createDirectionalLightVisualization(
				light as DirectionalLight,
				scene,
				radius,
				visible,
				quality
			);

		case LightType.Point:
			return createPointLightVisualization(
				light as PointLight,
				scene,
				radius,
				visible,
				quality
			);

		case LightType.Ambient:
			return createAmbientLightVisualization(
				light as AmbientLight,
				scene,
				radius,
				visible,
				quality
			);

		case LightType.Hemisphere:
			return createHemisphereLightVisualization(
				light as HemisphereLight,
				scene,
				radius,
				visible,
				quality
			);

		case LightType.RectArea:
			return createRectAreaLightVisualization(
				light as RectAreaLight,
				scene,
				radius,
				visible,
				quality
			);

		default:
			// Fallback to point light visualization for unknown types
			console.warn(`Unknown light type: ${lightType}, using PointLight visualization`);
			return createPointLightVisualization(
				light as PointLight,
				scene,
				radius,
				visible,
				quality
			);
	}
};

/**
 * Updates visualization objects for any light type
 *
 * @param light The light being visualized
 * @param objects The visualization objects to update
 * @param height Height for cone (SpotLight only)
 * @param visible Visibility state
 */
export const updateLightVisualization = (
	light: VisualizableLight,
	objects: LightHelperObjects,
	height: number,
	visible: boolean
): void => {
	const lightType = getLightType(light);

	switch (lightType) {
		case LightType.Spot:
			updateSpotLightVisualization(light as SpotLight, objects as any, height, visible);
			break;

		case LightType.Directional:
			updateDirectionalLightVisualization(light as DirectionalLight, objects as any, visible);
			break;

		case LightType.Point:
			updatePointLightVisualization(light as PointLight, objects as any, visible);
			break;

		case LightType.Ambient:
			updateAmbientLightVisualization(light as AmbientLight, objects as any, visible);
			break;

		case LightType.Hemisphere:
			updateHemisphereLightVisualization(light as HemisphereLight, objects as any, visible);
			break;

		case LightType.RectArea:
			updateRectAreaLightVisualization(light as RectAreaLight, objects as any, visible);
			break;

		default:
			// Fallback to point light update
			updatePointLightVisualization(light as PointLight, objects as any, visible);
			break;
	}
};

/**
 * Disposes visualization objects for any light type
 *
 * @param light The light being visualized
 * @param objects The visualization objects to dispose
 * @param scene Scene to remove objects from
 */
export const disposeLightVisualization = (
	light: VisualizableLight,
	objects: LightHelperObjects,
	scene: Scene
): void => {
	const lightType = getLightType(light);

	switch (lightType) {
		case LightType.Spot:
			disposeSpotLightVisualization(objects as any, scene);
			break;

		case LightType.Directional:
			disposeDirectionalLightVisualization(objects as any, scene);
			break;

		case LightType.Point:
			disposePointLightVisualization(objects as any, scene);
			break;

		case LightType.Ambient:
			disposeAmbientLightVisualization(objects as any, scene);
			break;

		case LightType.Hemisphere:
			disposeHemisphereLightVisualization(objects as any, scene);
			break;

		case LightType.RectArea:
			disposeRectAreaLightVisualization(objects as any, scene);
			break;

		default:
			// Fallback to point light dispose
			disposePointLightVisualization(objects as any, scene);
			break;
	}
};

